/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';

/**
 * @hook usePostMessage
 *
 * Custom hook that sets an event listener to return PostMessage
 * strings sent from the specified origin URL. The origin URL is
 * allowed to initially be null and be updated to the desired value,
 * or it may be initially set to the desired value. The PostMessage
 * strings to be matched against are assumed to never change.
 *
 * ```typescript
 *
 *   import {usePostMessage} from '@ion/dls/src/hooks/';
 *
 *   const SomeComponent = () => {
 *     const message1 = usePostMessage(
 *       'https://someserver.com',
 *       ['message A', 'message B'] // subscribe for specific messages
 *     );

 *     const message2 = usePostMessage(
 *       'https://someserver.com' // subscribe for all messages
 *     );
 *   }
 *
 * ```
 */
export function usePostMessage(messageOrigin: string, targetMessages?: any[]) {
  const [message, setMessage] = useState(null);

  const messageHandler = ({origin, data}) => {
    if (origin === messageOrigin) {
      if (!targetMessages || targetMessages.includes(data)) {
        setMessage(data);
      }
    }
  };

  useEffect(() => {
    if (messageOrigin) {
      window.addEventListener('message', messageHandler);
    }
    return () => {
      if (messageOrigin) {
        window.removeEventListener('message', messageHandler);
      }
    };
  }, [messageOrigin]);

  return message;
};
